import React, { useState, useEffect } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useAuth } from "../../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import {
  Search,
  Plus,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  TrendingUp,
  Target,
  Database,
  Activity,
  Filter
} from "lucide-react";
import { keywordDiscoveryService } from "../../services/keywordDiscoveryService";
import { formatDate } from "../../utils/dateUtils";
import {
  PpcFarmKeywordDocumentClient,
  PpcFarmKeywordStats,
  KeywordValidationResult,
  PpcFarmKeywordFilters,
  PpcFarmKeywordSortOptions
} from "../../types/ppcFarmTypes";

const KeywordDiscoveryPage: React.FC = () => {
  usePageTitle("Keyword Discovery | PPC Farm Mail Tracker");
  const { userEmail } = useAuth();

  // State for keyword statistics
  const [keywordStats, setKeywordStats] = useState<PpcFarmKeywordStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // State for keyword input
  const [keywordInput, setKeywordInput] = useState("");
  const [validationResults, setValidationResults] = useState<KeywordValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // State for keyword list
  const [keywords, setKeywords] = useState<PpcFarmKeywordDocumentClient[]>([]);
  const [keywordsLoading, setKeywordsLoading] = useState(true);
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  // State for filters and sorting
  const [filters, setFilters] = useState<Partial<PpcFarmKeywordFilters>>({
    search: "",
    status: "all",
    created_by: ""
  });
  const [sortOptions, setSortOptions] = useState<PpcFarmKeywordSortOptions>({
    field: "created_at",
    direction: "desc"
  });

  // Fetch keyword statistics on component mount
  useEffect(() => {
    const fetchKeywordStats = async () => {
      try {
        setStatsLoading(true);
        setStatsError(null);
        const stats = await keywordDiscoveryService.getKeywordStats();
        setKeywordStats(stats);
      } catch (error) {
        console.error("Error fetching keyword stats:", error);
        setStatsError("Failed to load keyword statistics");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchKeywordStats();
  }, []);

  // Fetch keywords with real-time updates
  useEffect(() => {
    setKeywordsLoading(true);
    
    const unsubscribe = keywordDiscoveryService.subscribeToKeywords(
      (updatedKeywords) => {
        setKeywords(updatedKeywords);
        setKeywordsLoading(false);
        setKeywordsError(null);
      },
      filters
    );

    return unsubscribe;
  }, [filters]);

  // Validate keywords when input changes
  useEffect(() => {
    const validateKeywords = async () => {
      if (!keywordInput.trim()) {
        setValidationResults([]);
        return;
      }

      setIsValidating(true);
      try {
        const keywords = keywordInput
          .split('\n')
          .map(k => k.trim())
          .filter(k => k.length > 0);

        if (keywords.length > 0) {
          const results = await keywordDiscoveryService.validateKeywords(keywords);
          setValidationResults(results);
        } else {
          setValidationResults([]);
        }
      } catch (error) {
        console.error("Error validating keywords:", error);
        setValidationResults([]);
      } finally {
        setIsValidating(false);
      }
    };

    const timeoutId = setTimeout(validateKeywords, 500); // Debounce validation
    return () => clearTimeout(timeoutId);
  }, [keywordInput]);

  // Statistics cards configuration
  const statsCards = [
    {
      title: "Total Keywords",
      value: keywordStats?.total_keywords?.toLocaleString() || "0",
      icon: Database,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      title: "Pending",
      value: keywordStats?.pending_keywords?.toLocaleString() || "0",
      icon: Clock,
      color: "text-yellow-600 dark:text-yellow-400",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
    },
    {
      title: "Completed",
      value: keywordStats?.completed_keywords?.toLocaleString() || "0",
      icon: CheckCircle,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      title: "Sellers Found",
      value: keywordStats?.total_sellers_found?.toLocaleString() || "0",
      icon: Target,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
  ];

  // Get status badge component
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300", icon: Clock },
      in_progress: { color: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300", icon: Activity },
      completed: { color: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300", icon: CheckCircle },
      error: { color: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300", icon: XCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return <Badge variant="secondary">{status}</Badge>;

    const IconComponent = config.icon;
    return (
      <Badge className={config.color}>
        <IconComponent className="h-3 w-3 mr-1" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  // Handle keyword submission
  const handleSubmitKeywords = async () => {
    if (!userEmail) return;

    const validKeywords = validationResults.filter(r => r.is_valid && !r.is_duplicate);
    if (validKeywords.length === 0) return;

    setIsSubmitting(true);
    try {
      const result = await keywordDiscoveryService.addKeywords({
        keywords: validKeywords.map(r => r.keyword),
        user_email: userEmail,
        source: 'manual_input'
      });

      if (result.success) {
        setKeywordInput("");
        setValidationResults([]);
        setShowConfirmDialog(false);
        
        // Refresh stats
        const stats = await keywordDiscoveryService.getKeywordStats();
        setKeywordStats(stats);
      }
    } catch (error) {
      console.error("Error adding keywords:", error);
    } finally {
      setIsSubmitting(false);
    }
  };


  // Get validation summary
  const getValidationSummary = () => {
    const valid = validationResults.filter(r => r.is_valid && !r.is_duplicate).length;
    const duplicates = validationResults.filter(r => r.is_duplicate).length;
    const invalid = validationResults.filter(r => !r.is_valid).length;
    
    return { valid, duplicates, invalid, total: validationResults.length };
  };

  const validationSummary = getValidationSummary();

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Keyword Discovery
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage keywords for Amazon seller discovery through the Keepa API
        </p>
      </div>

      {/* Keyword Statistics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Discovery Statistics
        </h2>

        {statsError ? (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="p-6">
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                <span>Error loading statistics: {statsError}</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {statsCards.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 flex items-center justify-center">
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-1">
                        <IconComponent className={`h-4 w-4 ${stat.color} mr-2`} />
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {stat.title}
                        </p>
                      </div>
                      {statsLoading ? (
                        <Skeleton className="h-6 w-16" />
                      ) : (
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {stat.value}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Keyword Input Section */}
      <div className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Plus className="h-5 w-5 mr-2" />
              Add Keywords
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Keywords (one per line)
              </label>
              <Textarea
                placeholder="Enter keywords, one per line&#10;Example:&#10;organic coffee beans&#10;wireless headphones&#10;yoga mats"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                className="min-h-[120px]"
                disabled={isSubmitting}
              />
              <div className="flex justify-between items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
                <span>
                  {keywordInput.split('\n').filter(k => k.trim().length > 0).length} keywords entered
                </span>
                {isValidating && (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    Validating...
                  </div>
                )}
              </div>
            </div>

            {/* Validation Results */}
            {validationResults.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900 dark:text-white">Validation Results</h4>
                  <div className="flex space-x-4 text-sm">
                    <span className="text-green-600 dark:text-green-400">
                      ✓ {validationSummary.valid} valid
                    </span>
                    {validationSummary.duplicates > 0 && (
                      <span className="text-yellow-600 dark:text-yellow-400">
                        ⚠ {validationSummary.duplicates} duplicates
                      </span>
                    )}
                    {validationSummary.invalid > 0 && (
                      <span className="text-red-600 dark:text-red-400">
                        ✗ {validationSummary.invalid} invalid
                      </span>
                    )}
                  </div>
                </div>

                {/* Detailed Validation Results */}
                {(validationSummary.invalid > 0 || validationSummary.duplicates > 0) && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Issues Found:
                    </h5>
                    <div className="space-y-1 text-sm">
                      {validationResults
                        .filter(result => !result.is_valid || result.is_duplicate)
                        .map((result, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <span className={`flex-shrink-0 ${
                              result.is_duplicate
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {result.is_duplicate ? '⚠' : '✗'}
                            </span>
                            <div className="flex-1">
                              <span className="font-medium text-gray-900 dark:text-white">
                                "{result.keyword}"
                              </span>
                              <span className={`ml-2 ${
                                result.is_duplicate
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}>
                                - {result.error_message}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {validationSummary.valid > 0 && (
                  <Button
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Adding Keywords...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add {validationSummary.valid} Valid Keywords
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Keywords List Section */}
      <div className="mb-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <Search className="h-5 w-5 mr-2" />
                Keyword Ledger
              </CardTitle>
              <div className="flex items-center space-x-2">
                <Input
                  placeholder="Search keywords..."
                  value={filters.search || ""}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-64"
                />
                <Select
                  value={filters.status || "all"}
                  onValueChange={(value) => setFilters({ ...filters, status: value as any })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {keywordsError ? (
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400 p-4">
                <AlertCircle className="h-5 w-5" />
                <span>Error loading keywords: {keywordsError}</span>
              </div>
            ) : keywordsLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : keywords.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No keywords found. Add some keywords to get started!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Keyword</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Sellers Found</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keywords.map((keyword) => (
                    <TableRow key={keyword.term}>
                      <TableCell className="font-medium">{keyword.term}</TableCell>
                      <TableCell>{getStatusBadge(keyword.status)}</TableCell>
                      <TableCell>{formatDate(keyword.created_at?.toDate() || new Date())}</TableCell>
                      <TableCell>
                        {keyword.stats?.sellers_found?.toLocaleString() || "0"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Keywords?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to add <strong>{validationSummary.valid}</strong> keywords to the discovery queue.
              {validationSummary.duplicates > 0 && (
                <span className="block mt-2 text-yellow-600">
                  Note: {validationSummary.duplicates} duplicate keywords will be skipped.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitKeywords}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                "Continue"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KeywordDiscoveryPage;